"use client";

import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useRouter } from "next/navigation";
import { Brain, X, Play, Coffee, Bell, BellOff, Clock, Volume2, VolumeX, Palette, Check, BookOpen } from "lucide-react";
import {
  FOCUS_PRESETS,
  BREAK_PRESETS,
  DEFAULT_FOCUS_MIN,
  DEFAULT_BREAK_MIN,
  resolveFocusMinutes,
  resolveBreakMinutes,
  autoBreaksEnabled,
  type FocusPreferences,
} from "@/lib/focus-plan";
import { startFocus, type FocusContext } from "@/lib/focus-timer-store";
import { useFocusMode } from "@/components/focus/useFocusState";
import { FOCUS_THEMES, getSavedFocusThemeId, saveFocusThemeId } from "@/lib/focus-themes";
import { isFocusSoundEnabled, setFocusSoundEnabled, primeAudio, playFocusStart } from "@/lib/focus-sounds";

interface OpenDetail {
  context?: Partial<FocusContext>;
}

interface ProgressCourse {
  courseId: string;
  slug: string;
  title: string;
  thumbnail: string;
  cardColor: string;
  progressPercent: number;
  resumeLessonId: string | null;
}

function continueHref(c: { slug: string; resumeLessonId: string | null }): string {
  return c.resumeLessonId ? `/kurs/${c.slug}/lekcja/${c.resumeLessonId}` : `/platforma/panel/kurs/${c.slug}`;
}

export default function FocusStartDialog() {
  const reduce = useReducedMotion();
  const router = useRouter();
  const active = useFocusMode() !== "idle";
  const [open, setOpen] = useState(false);
  const [focusMin, setFocusMin] = useState(DEFAULT_FOCUS_MIN);
  const [breakMin, setBreakMin] = useState(DEFAULT_BREAK_MIN);
  const [autoBreaks, setAutoBreaks] = useState(true);
  const [ctx, setCtx] = useState<Partial<FocusContext> | null>(null);
  const [customFocus, setCustomFocus] = useState(false);
  const [courses, setCourses] = useState<ProgressCourse[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);
  const [themeId, setThemeId] = useState<string>("calm-green");
  const [soundOn, setSoundOn] = useState(true);

  const loadDefaults = useCallback(async () => {
    setThemeId(getSavedFocusThemeId());
    setSoundOn(isFocusSoundEnabled());
    try {
      const data = await fetch("/api/auth/preferences").then((r) => (r.ok ? r.json() : null));
      const prefs = (data?.preferences ?? null) as FocusPreferences | null;
      setFocusMin(resolveFocusMinutes(prefs));
      setBreakMin(resolveBreakMinutes(prefs));
      setAutoBreaks(autoBreaksEnabled(prefs));
      setCustomFocus(!FOCUS_PRESETS.includes(resolveFocusMinutes(prefs)));
    } catch {
      /* zostaw domyślne */
    }
  }, []);

  const loadCourses = useCallback(async (preselectSlug?: string) => {
    try {
      const data = await fetch("/api/auth/progress").then((r) => (r.ok ? r.json() : null));
      const list: ProgressCourse[] = Array.isArray(data?.courses) ? data.courses : [];
      const inProgress = list
        .filter((c) => c.progressPercent < 100)
        .sort((a, b) => b.progressPercent - a.progressPercent);
      const pool = inProgress.length > 0 ? inProgress : list;
      setCourses(pool.slice(0, 6));
      const pre = preselectSlug ? pool.find((c) => c.slug === preselectSlug) : pool[0];
      setSelectedCourseId(pre ? pre.courseId : null);
    } catch {
      setCourses([]);
    }
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<OpenDetail>).detail;
      setCtx(detail?.context ?? null);
      loadDefaults();
      loadCourses(detail?.context?.courseSlug);
      setOpen(true);
    };
    window.addEventListener("ns:open-focus-start", handler as EventListener);
    return () => window.removeEventListener("ns:open-focus-start", handler as EventListener);
  }, [loadDefaults, loadCourses]);

  const start = () => {
    const course = courses.find((c) => c.courseId === selectedCourseId) ?? null;

    // Zapisz wybory (motyw + dźwięk) i odblokuj dźwięk w geście kliknięcia.
    saveFocusThemeId(themeId);
    setFocusSoundEnabled(soundOn);
    primeAudio();
    if (soundOn) playFocusStart();
    // Poproś o zgodę na powiadomienia przeglądarki (gest użytkownika).
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
      try {
        void Notification.requestPermission();
      } catch {
        /* ignore */
      }
    }

    let context: FocusContext | null = null;
    let navigateTo: string | null = null;
    if (course) {
      // Jeśli otwarto z lekcji tego samego kursu — zostań na bieżącej lekcji
      // (nie przeskakuj do innej „wznów"). Inaczej przejdź do wznowienia kursu.
      const sameAsContext = !!(ctx?.courseSlug && ctx.courseSlug === course.slug && ctx.returnHref);
      const href = sameAsContext ? ctx!.returnHref! : continueHref(course);
      navigateTo = sameAsContext ? null : href;
      context = {
        courseSlug: course.slug,
        courseId: course.courseId,
        courseTitle: course.title,
        lessonId: sameAsContext ? ctx!.lessonId ?? course.resumeLessonId ?? "" : course.resumeLessonId ?? "",
        lessonTitle: sameAsContext ? ctx!.lessonTitle ?? "" : "",
        returnHref: href,
        subjectLabel: sameAsContext ? ctx!.subjectLabel || course.title : course.title,
      };
    } else if (ctx) {
      context = {
        courseSlug: ctx.courseSlug ?? "",
        courseId: ctx.courseId ?? "",
        courseTitle: ctx.courseTitle ?? "",
        lessonId: ctx.lessonId ?? "",
        lessonTitle: ctx.lessonTitle ?? "",
        returnHref: ctx.returnHref ?? (typeof window !== "undefined" ? window.location.pathname : ""),
        subjectLabel: ctx.subjectLabel ?? "",
      };
    }

    startFocus({ focusMs: focusMin * 60000, breakMs: breakMin * 60000, autoBreaks, context });
    setOpen(false);
    // Przekieruj ucznia prosto do wybranego kursu.
    if (navigateTo && typeof window !== "undefined" && window.location.pathname !== navigateTo) {
      router.push(navigateTo);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="focus-start"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[95] flex items-end justify-center p-0 sm:items-center sm:p-4"
        >
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <motion.div
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 40, scale: 0.98 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 40, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900 sm:max-w-md sm:rounded-3xl"
          >
            {/* Nagłówek */}
            <div className="relative overflow-hidden bg-gradient-to-br from-primary via-blue-600 to-indigo-600 px-5 pb-6 pt-5 text-white">
              <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10 blur-xl" />
              <div className="relative flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15">
                    <Brain size={18} />
                  </span>
                  <div>
                    <p className="text-sm font-bold leading-tight">Sesja skupienia</p>
                    <p className="text-[11px] text-white/70">Wybierz kurs i swój rytm</p>
                  </div>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Zamknij"
                  className="cursor-pointer rounded-lg p-1.5 transition-colors hover:bg-white/15"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="space-y-5 overflow-y-auto p-5">
              {active && (
                <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                  Masz już aktywną sesję — rozpoczęcie nowej zastąpi bieżącą.
                </p>
              )}

              {/* Wybór kursu */}
              {courses.length > 0 && (
                <div>
                  <label className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    <BookOpen size={13} /> Co chcesz robić?
                  </label>
                  <div className="space-y-2">
                    {courses.map((c) => {
                      const activeSel = selectedCourseId === c.courseId;
                      const pct = Math.min(100, Math.max(0, c.progressPercent));
                      return (
                        <button
                          key={c.courseId}
                          type="button"
                          onClick={() => setSelectedCourseId(c.courseId)}
                          className={`flex w-full items-center gap-3 rounded-xl border p-2.5 text-left transition-all ${
                            activeSel
                              ? "border-primary bg-primary/5 ring-1 ring-primary/30 dark:bg-primary/10"
                              : "border-slate-200 hover:border-primary/40 dark:border-slate-700"
                          }`}
                        >
                          <span
                            className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg text-white"
                            style={{ backgroundColor: c.cardColor || "#6366f1" }}
                          >
                            {c.thumbnail ? (
                              // eslint-disable-next-line @next/next/no-img-element
                              <img src={c.thumbnail} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <BookOpen size={16} />
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold text-slate-800 dark:text-white">{c.title}</span>
                            <span className="mt-0.5 block text-[11px] text-slate-400">{pct}% ukończone</span>
                          </span>
                          {activeSel && <Check size={16} className="shrink-0 text-primary" />}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => setSelectedCourseId(null)}
                      className={`w-full rounded-xl border px-3 py-2 text-left text-sm transition-all ${
                        selectedCourseId === null
                          ? "border-primary bg-primary/5 text-slate-700 ring-1 ring-primary/30 dark:bg-primary/10 dark:text-white"
                          : "border-slate-200 text-slate-500 hover:border-primary/40 dark:border-slate-700 dark:text-slate-400"
                      }`}
                    >
                      Zostań tutaj (bez przechodzenia do kursu)
                    </button>
                  </div>
                </div>
              )}

              {/* Czas skupienia */}
              <div>
                <label className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <Brain size={13} /> Czas skupienia
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {FOCUS_PRESETS.map((m) => (
                    <PresetButton
                      key={m}
                      active={!customFocus && focusMin === m}
                      onClick={() => {
                        setCustomFocus(false);
                        setFocusMin(m);
                      }}
                      label={`${m} min`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setCustomFocus(true)}
                  className={`mt-2 flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-all ${
                    customFocus
                      ? "border-primary bg-primary/5 dark:bg-primary/10"
                      : "border-slate-200 dark:border-slate-700"
                  }`}
                >
                  <Clock size={14} className="text-slate-400" />
                  <span className="text-slate-600 dark:text-slate-300">Własny czas</span>
                  {customFocus && (
                    <input
                      type="number"
                      min={5}
                      max={180}
                      value={focusMin}
                      onChange={(e) => setFocusMin(Math.min(180, Math.max(5, Number(e.target.value) || 0)))}
                      onClick={(e) => e.stopPropagation()}
                      className="ml-auto w-16 rounded-lg border border-slate-200 bg-white px-2 py-1 text-right text-sm dark:border-slate-700 dark:bg-slate-800"
                    />
                  )}
                  {customFocus && <span className="text-xs text-slate-400">min</span>}
                </button>
              </div>

              {/* Czas przerwy */}
              <div>
                <label className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <Coffee size={13} /> Przerwa
                </label>
                <div className="flex flex-wrap gap-2">
                  {BREAK_PRESETS.map((m) => (
                    <PresetButton key={m} active={breakMin === m} onClick={() => setBreakMin(m)} label={`${m} min`} compact />
                  ))}
                </div>
              </div>

              {/* Motyw kolorystyczny */}
              <div>
                <label className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <Palette size={13} /> Tryb kolorystyczny
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {FOCUS_THEMES.map((t) => {
                    const activeTheme = themeId === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setThemeId(t.id)}
                        title={`${t.label} — ${t.description}`}
                        className={`flex flex-col items-center gap-1 rounded-xl border p-1.5 transition-all ${
                          activeTheme ? "border-primary ring-1 ring-primary/30" : "border-slate-200 hover:border-primary/40 dark:border-slate-700"
                        }`}
                      >
                        <span
                          className="relative flex h-8 w-full items-center justify-center rounded-lg"
                          style={{ backgroundColor: t.swatch.bg }}
                        >
                          <span className="h-3.5 w-3.5 rounded-full" style={{ backgroundColor: t.swatch.accent }} />
                          {activeTheme && (
                            <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-white">
                              <Check size={10} strokeWidth={3} />
                            </span>
                          )}
                        </span>
                        <span className="text-[9px] font-medium leading-tight text-slate-500 dark:text-slate-400">{t.label.split(" ")[0]}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Automatyczne przerwy + dźwięk */}
              <div className="space-y-2">
                <ToggleRow
                  on={autoBreaks}
                  onToggle={() => setAutoBreaks((v) => !v)}
                  icon={autoBreaks ? <Bell size={15} /> : <BellOff size={15} />}
                  title="Automatyczne przerwy"
                  desc="Przypomnimy delikatnie o przerwie"
                />
                <ToggleRow
                  on={soundOn}
                  onToggle={() => setSoundOn((v) => !v)}
                  icon={soundOn ? <Volume2 size={15} /> : <VolumeX size={15} />}
                  title="Efekty dźwiękowe"
                  desc="Delikatny sygnał po przerwie i sesji"
                />
              </div>
            </div>

            <div className="shrink-0 border-t border-slate-100 p-4 dark:border-slate-800">
              <button
                onClick={start}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-primary-dark"
              >
                <Play size={16} /> Rozpocznij {focusMin} min skupienia
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ToggleRow({
  on,
  onToggle,
  icon,
  title,
  desc,
}: {
  on: boolean;
  onToggle: () => void;
  icon: React.ReactNode;
  title: string;
  desc: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3.5 py-2.5 text-left transition-colors hover:border-primary/40 dark:border-slate-700"
    >
      <div className="flex items-center gap-2.5">
        <span
          className={`flex h-8 w-8 items-center justify-center rounded-lg ${
            on ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-400 dark:bg-slate-800"
          }`}
        >
          {icon}
        </span>
        <div>
          <p className="text-sm font-semibold text-slate-800 dark:text-white">{title}</p>
          <p className="text-[11px] text-slate-400">{desc}</p>
        </div>
      </div>
      <span className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${on ? "bg-primary" : "bg-slate-300 dark:bg-slate-600"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
      </span>
    </button>
  );
}

function PresetButton({
  active,
  onClick,
  label,
  compact,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`cursor-pointer rounded-xl border text-sm font-semibold transition-all ${compact ? "px-3.5 py-1.5" : "py-2.5"} ${
        active
          ? "border-primary bg-primary text-white shadow-sm"
          : "border-slate-200 bg-white text-slate-600 hover:border-primary/40 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
      }`}
    >
      {label}
    </button>
  );
}
